import * as THREE from 'three';
import { T_PATH, T_SPAWN, T_VOID, buildTerrain, cellId, hexToWorld, type Hex, type MapDef } from '@somnhold/sim';
import { WORLD_PER_HEX } from './coords.ts';
import { walledDist } from './wallgraph.ts';

/**
 * The land outside the walls: one continuous low-poly ground (no hex grid) with gentle hills,
 * ravines and worn dirt roads along the sector paths, sparse trees near the roads that thicken
 * into woods toward the edge, and a bank of drifting fog that swallows the edge of the map and
 * every road's mouth, so nightmares walk out of the mist. Decoration only: the sim knows none of it.
 */

const K = WORLD_PER_HEX / 1000;
/** Ground height under a hex tile: hidden, well below the tiles' tops (their sides reach down to −0.8). */
const UNDER_TILE = -0.15;
const TAU = Math.PI * 2;
/** How many slices around the bastion each kind of the woods comes in (outskirts add()). */
const SLICES = 12;
const clamp01 = (x: number): number => (x < 0 ? 0 : x > 1 ? 1 : x);
const smooth = (a: number, b: number, x: number): number => {
  const t = clamp01((x - a) / (b - a));
  return t * t * (3 - 2 * t);
};

/** Seeded PRNG (render side only). */
const prng = (seed: number): (() => number) => {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

/** Seeded 2D value noise with fractal octaves, roughly in [-0.7, 0.7]. */
const makeNoise = (seed: number): ((x: number, y: number, oct?: number) => number) => {
  const h = (ix: number, iy: number): number => {
    let n = (Math.imul(ix, 374761393) + Math.imul(iy, 668265263) + Math.imul(seed, 1442695041)) | 0;
    n = Math.imul(n ^ (n >>> 13), 1274126177);
    n ^= n >>> 16;
    return ((n >>> 0) / 4294967296) * 2 - 1;
  };
  const v = (x: number, y: number): number => {
    const ix = Math.floor(x);
    const iy = Math.floor(y);
    const fx = x - ix;
    const fy = y - iy;
    const ux = fx * fx * (3 - 2 * fx);
    const uy = fy * fy * (3 - 2 * fy);
    const a = h(ix, iy);
    const b = h(ix + 1, iy);
    const c = h(ix, iy + 1);
    const d = h(ix + 1, iy + 1);
    return a + (b - a) * ux + (c - a) * uy + (a - b - c + d) * ux * uy;
  };
  return (x, y, oct = 3) => {
    let s = 0;
    let amp = 0.5;
    let f = 1;
    let norm = 0;
    for (let i = 0; i < oct; i++) {
      s += v(x * f + i * 17.3, y * f - i * 9.1) * amp;
      norm += amp;
      amp *= 0.5;
      f *= 2.03;
    }
    return s / norm;
  };
};

const cellXZ = (h: Hex): THREE.Vector2 => {
  const c = hexToWorld(h.q, h.r);
  return new THREE.Vector2(c.x * K, c.y * K);
};

const COL = {
  grassA: new THREE.Color('#323a29'),
  grassB: new THREE.Color('#3b4330'),
  moss: new THREE.Color('#2f4429'),
  heath: new THREE.Color('#3e3a33'),
  stone: new THREE.Color('#585a58'),
  ravine: new THREE.Color('#283030'),
  floor: new THREE.Color('#1f2619'),
  road: new THREE.Color('#6b5b49'),
  roadB: new THREE.Color('#5f5040'),
  verge: new THREE.Color('#3f3527'),
  rut: new THREE.Color('#4e3e2b'),
  apron: new THREE.Color('#4d4a43'),
};

/** Fog palette: a cold moonlit mist, darkest where it closes over the edge of the world. */
export const MIST = {
  low: new THREE.Color('#5d6573'),
  mid: new THREE.Color('#444b57'),
  far: new THREE.Color('#1b1e24'),
};

const FOG_VERT = /* glsl */ `
  varying vec2 vXZ;
  void main() {
    vec4 w = modelMatrix * vec4(position, 1.0);
    vXZ = w.xz;
    gl_Position = projectionMatrix * viewMatrix * w;
  }`;

const FOG_FRAG = /* glsl */ `
  uniform sampler2D tNoise;
  uniform float uTime;
  uniform vec3 uColor;
  uniform float uAlpha;
  uniform float uBase;
  uniform float uBand;
  uniform float uOut;
  uniform float uOpaque;
  uniform float uSeed;
  uniform vec4 uSpawn[8];
  varying vec2 vXZ;
  float angDist(float a, float b) { float d = abs(a - b); return min(d, 6.2831853 - d); }
  void main() {
    float r = length(vXZ);
    float th = atan(vXZ.y, vXZ.x);
    float rin = uBase;
    for (int i = 0; i < 8; i++) {
      vec4 s = uSpawn[i];
      if (s.z > 0.5) rin = min(rin, s.x - s.w + angDist(th, s.y) * s.x * 0.9);
    }
    vec2 p = vXZ + uSeed;
    float n1 = texture2D(tNoise, p * 0.04 + vec2(uTime * 0.011, uTime * 0.004)).r;
    float n2 = texture2D(tNoise, p * 0.105 + vec2(-uTime * 0.007, uTime * 0.013)).r;
    float n = n1 * 0.62 + n2 * 0.38;
    float f = smoothstep(rin - 0.6, rin + uBand, r + (n - 0.5) * uBand * 1.5);
    float a = f * uAlpha * (0.45 + 1.1 * n);
    a = max(a, smoothstep(uOut - 6.0, uOut, r) * uOpaque);
    gl_FragColor = vec4(uColor, clamp(a, 0.0, 1.0));
    #include <tonemapping_fragment>
    #include <colorspace_fragment>
  }`;

/** Tileable value-noise texture for the fog (generated, never downloaded). */
const fogNoise = (seed: number): THREE.DataTexture => {
  const N = 128;
  const data = new Uint8Array(N * N * 4);
  const rnd = prng(seed ^ 0xf06);
  const octaves = [4, 8, 16, 32].map((p) => ({ p, g: Array.from({ length: p * p }, () => rnd()) }));
  for (let y = 0; y < N; y++) {
    for (let x = 0; x < N; x++) {
      let s = 0;
      let amp = 0.5;
      let norm = 0;
      for (const { p, g } of octaves) {
        const fx = (x / N) * p;
        const fy = (y / N) * p;
        const ix = Math.floor(fx);
        const iy = Math.floor(fy);
        const tx = fx - ix;
        const ty = fy - iy;
        const ux = tx * tx * (3 - 2 * tx);
        const uy = ty * ty * (3 - 2 * ty);
        const at = (i: number, j: number): number => g[((j % p) * p + (i % p)) % (p * p)] as number;
        const a = at(ix, iy);
        const b = at(ix + 1, iy);
        const c = at(ix, iy + 1);
        const d = at(ix + 1, iy + 1);
        s += (a + (b - a) * ux + (c - a) * uy + (a - b - c + d) * ux * uy) * amp;
        norm += amp;
        amp *= 0.55;
      }
      const v = Math.round(clamp01(((s / norm - 0.5) * 1.6 + 0.5)) * 255);
      const o = (y * N + x) * 4;
      data[o] = data[o + 1] = data[o + 2] = v;
      data[o + 3] = 255;
    }
  }
  const tex = new THREE.DataTexture(data, N, N, THREE.RGBAFormat);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.magFilter = THREE.LinearFilter;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.generateMipmaps = true;
  tex.needsUpdate = true;
  return tex;
};

/** The fog bank: a few stacked translucent rings over the edge, drifting slowly. */
export class FogBank {
  readonly group = new THREE.Group();
  private mats: THREE.ShaderMaterial[] = [];
  /** Which palette entry each layer was painted with (so a season can repaint it). */
  private kinds: ('low' | 'mid' | 'far')[] = [];

  constructor(spawns: THREE.Vector2[], base: number, outer: number, low: boolean, seed: number) {
    this.group.name = 'fog';
    const noise = fogNoise(seed);
    const sp = Array.from({ length: 8 }, (_, i) => {
      const s = spawns[i];
      return s ? new THREE.Vector4(s.length(), Math.atan2(s.y, s.x), 1, 2.6) : new THREE.Vector4(0, 0, 0, 0);
    });
    let inner = base;
    for (const s of spawns) inner = Math.min(inner, s.length() - 2.6);
    const layers = low
      ? [
          { y: 0.35, a: 0.8, c: MIST.low, o: 0, band: 5 },
          { y: 2.2, a: 0.55, c: MIST.far, o: 1, band: 7 },
        ]
      : [
          { y: 0.12, a: 0.55, c: MIST.low, o: 0, band: 4.5 },
          { y: 0.6, a: 0.5, c: MIST.low, o: 0, band: 5 },
          { y: 1.25, a: 0.45, c: MIST.mid, o: 0, band: 6 },
          { y: 2.3, a: 0.5, c: MIST.far, o: 1, band: 8 },
        ];
    layers.forEach((l, i) => {
      const mat = new THREE.ShaderMaterial({
        vertexShader: FOG_VERT,
        fragmentShader: FOG_FRAG,
        transparent: true,
        depthWrite: false,
        toneMapped: false,
        uniforms: {
          tNoise: { value: noise },
          uTime: { value: 0 },
          uColor: { value: l.c.clone() },
          uAlpha: { value: l.a },
          uBase: { value: base },
          uBand: { value: l.band },
          uOut: { value: outer },
          uOpaque: { value: l.o },
          uSeed: { value: i * 13.7 + (seed % 97) },
          uSpawn: { value: sp },
        },
      });
      this.mats.push(mat);
      this.kinds.push(l.c === MIST.low ? 'low' : l.c === MIST.mid ? 'mid' : 'far');
      const geo = new THREE.RingGeometry(Math.max(1, inner - 1.5), outer + 150, 96, 6).rotateX(-Math.PI / 2);
      const m = new THREE.Mesh(geo, mat);
      m.position.y = l.y;
      m.renderOrder = 5;
      m.frustumCulled = false;
      this.group.add(m);
    });
  }

  update(t: number): void {
    for (const m of this.mats) (m.uniforms.uTime as { value: number }).value = t;
  }

  /** Repaints the bank (a holiday's mist): a color per layer kind and a thickness multiplier. */
  tint(c: { low: string; mid: string; far: string; alpha: number }): void {
    this.mats.forEach((m, i) => {
      (m.uniforms.uColor as { value: THREE.Color }).value.set(c[this.kinds[i] ?? 'far']);
      const a = m.uniforms.uAlpha as { value: number };
      a.value = Math.min(1, a.value * c.alpha);
    });
  }
}

/** Two rounds of Chaikin corner cutting: the hex zigzag becomes a winding track. */
const chaikin = (pts: THREE.Vector2[]): THREE.Vector2[] => {
  let cur = pts;
  for (let k = 0; k < 2; k++) {
    const out: THREE.Vector2[] = [cur[0] as THREE.Vector2];
    for (let i = 0; i < cur.length - 1; i++) {
      const a = cur[i] as THREE.Vector2;
      const b = cur[i + 1] as THREE.Vector2;
      out.push(a.clone().lerp(b, 0.25), a.clone().lerp(b, 0.75));
    }
    out.push(cur[cur.length - 1] as THREE.Vector2);
    cur = out;
  }
  return cur;
};

const roadRibbon = (
  roads: THREE.Vector2[][],
  ground: (x: number, z: number) => number,
  noise: (x: number, y: number, oct?: number) => number,
  B: number,
  hexDistAt: (x: number, z: number) => number,
): THREE.Mesh => {
  const pos: number[] = [];
  const col: number[] = [];
  const idx: number[] = [];
  const across = [-0.62, -0.36, -0.2, 0.2, 0.36, 0.62];
  const alpha = [0, 1, 1, 1, 1, 0];
  const c = new THREE.Color();
  for (const raw of roads) {
    const pts = chaikin(raw).filter((p) => hexDistAt(p.x, p.y) > B + 0.55);
    if (pts.length < 2) continue;
    const base = pos.length / 3;
    pts.forEach((p, i) => {
      const a = pts[Math.max(0, i - 1)] as THREE.Vector2;
      const b = pts[Math.min(pts.length - 1, i + 1)] as THREE.Vector2;
      const t = b.clone().sub(a).normalize();
      const nx = -t.y;
      const nz = t.x;
      const w = 0.92 + noise(p.x * 0.8, p.y * 0.8, 2) * 0.35;
      across.forEach((o, j) => {
        const x = p.x + nx * o * w;
        const z = p.y + nz * o * w;
        // Lifted clear of the faceted ground (whose flat facets can sit a little above the smooth height between vertices).
        pos.push(x, ground(x, z) + 0.03, z);
        const nn = noise(x * 1.7 + 5, z * 1.7 - 9, 2);
        c.copy(nn > 0 ? COL.road : COL.roadB).multiplyScalar(1 + nn * 0.25);
        // Wheel ruts and the trodden middle.
        if (j === 2 || j === 3) c.lerp(COL.rut, 0.18);
        col.push(c.r, c.g, c.b, (alpha[j] as number) * (0.9 + nn * 0.1));
      });
    });
    const S = across.length;
    for (let i = 0; i < pts.length - 1; i++) {
      for (let j = 0; j < S - 1; j++) {
        const a = base + i * S + j;
        const b = a + 1;
        const d = a + S;
        const e = d + 1;
        idx.push(a, b, d, b, e, d);
      }
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  geo.setAttribute('color', new THREE.Float32BufferAttribute(col, 4));
  geo.setIndex(idx);
  geo.computeVertexNormals();
  const mat = new THREE.MeshStandardMaterial({
    vertexColors: true,
    transparent: true,
    depthWrite: false,
    roughness: 1,
    metalness: 0,
    polygonOffset: true,
    polygonOffsetFactor: -1,
    polygonOffsetUnits: -2,
  });
  const m = new THREE.Mesh(geo, mat);
  m.name = 'roads';
  return m;
};

export interface Outskirts {
  /** Ground height (world units) at a world XZ point outside the walls. */
  ground: (x: number, z: number) => number;
  /** Distance (world units) from a world XZ point to the nearest road's center line. */
  roadDist: (x: number, z: number) => number;
  group: THREE.Group;
  fog: FogBank;
}

/**
 * Builds the ground, the roads, the woods and the fog for a bastion or incursion map.
 * `inner` is the hex radius already covered by hex tiles (the walls or the clearing).
 */
export const buildOutskirts = (map: MapDef, seed: number, low: boolean): Outskirts => {
  const B = map.bastion;
  // Distance to the walled ground (the ring and its districts), in hexes: B on the wall line.
  const hexDistAt = walledDist(map);
  const terrain = buildTerrain(map);
  /**
   * Whether a world XZ point lies on a hex tile (inside the walls or the clearing). The ground's mesh
   * hides well under the tiles there: at their very height it fought them for depth on phones, in
   * triangles across the edge tiles (the distance in hexes alone can't tell, near a tile's corners).
   */
  const onTile = (x: number, z: number): boolean => {
    const fr = z / (WORLD_PER_HEX * 0.866);
    const fq = x / WORLD_PER_HEX - fr / 2;
    let q = Math.round(fq);
    let r = Math.round(fr);
    const s = Math.round(-fq - fr);
    const dq = Math.abs(q - fq);
    const dr = Math.abs(r - fr);
    const ds = Math.abs(s + fq + fr);
    if (dq > dr && dq > ds) q = -r - s;
    else if (dr > ds) r = -q - s;
    const id = cellId(map.radius, q, r);
    const t = id >= 0 ? terrain[id] : T_VOID;
    return t !== T_VOID && t !== T_PATH && t !== T_SPAWN;
  };
  const noise = makeNoise(seed ^ 0x51ed);
  const noise2 = makeNoise(seed ^ 0x2a1b);
  const rnd = prng(seed ^ 0x7ee5);

  // Roads: gate → path cells → spawn, then on out into the fog (decor only past the spawn).
  const roads: THREE.Vector2[][] = [];
  const spawns: THREE.Vector2[] = [];
  for (const sec of map.sectors) {
    const cells = sec.path.slice().reverse();
    const pts = [cellXZ(sec.gate), ...cells.map(cellXZ)];
    const s0 = pts[pts.length - 1] as THREE.Vector2;
    const s1 = (pts[pts.length - 2] ?? new THREE.Vector2()) as THREE.Vector2;
    spawns.push(s0.clone());
    const dir = s0.clone().sub(s1).normalize();
    const p = s0.clone();
    for (let i = 0; i < 9; i++) {
      const radial = p.clone().normalize();
      const perp = new THREE.Vector2(-radial.y, radial.x);
      dir.multiplyScalar(0.72).addScaledVector(radial, 0.28).addScaledVector(perp, (rnd() - 0.5) * 0.35).normalize();
      p.addScaledVector(dir, 1.05);
      pts.push(p.clone());
    }
    roads.push(pts);
  }
  // Flat (ax, az, bx, bz) runs: the ground under every moving piece asks this each frame, and a loop over
  // tuples allocated an iterator per segment.
  const segs: number[] = [];
  for (const pts of roads) for (let i = 1; i < pts.length; i++) segs.push((pts[i - 1] as THREE.Vector2).x, (pts[i - 1] as THREE.Vector2).y, (pts[i] as THREE.Vector2).x, (pts[i] as THREE.Vector2).y);
  const roadDist = (x: number, z: number): number => {
    let best = 1e9;
    for (let i = 0; i < segs.length; i += 4) {
      const ax = segs[i] as number;
      const az = segs[i + 1] as number;
      const bx = segs[i + 2] as number;
      const bz = segs[i + 3] as number;
      const dx = bx - ax;
      const dz = bz - az;
      const t = clamp01(((x - ax) * dx + (z - az) * dz) / (dx * dx + dz * dz || 1));
      const ex = x - (ax + dx * t);
      const ez = z - (az + dz * t);
      const d = ex * ex + ez * ez;
      if (d < best) best = d;
    }
    return Math.sqrt(best);
  };

  // Where the fog begins (world radius), per direction: it reaches in to swallow each road's mouth.
  let base = 0;
  for (const s of spawns) base = Math.max(base, s.length() + 0.4);
  if (spawns.length === 0) base = (map.radius - 2) * WORLD_PER_HEX;
  const fogIn = (x: number, z: number): number => {
    const th = Math.atan2(z, x);
    let rin = base;
    for (const s of spawns) {
      let d = Math.abs(th - Math.atan2(s.y, s.x));
      d = Math.min(d, TAU - d);
      rin = Math.min(rin, s.length() - 2.6 + d * s.length() * 0.9);
    }
    return rin;
  };
  const outer = base + 7;
  const extent = outer + 1.5;

  /** Relief and road carving; `pd` is the distance to the nearest road. */
  const heightWith = (x: number, z: number, pd: number): number => {
    const dh = hexDistAt(x, z);
    // At the foot of the walls the ground rises to the tiles' edge, so no hex skirt shows.
    if (dh < B + 0.7) return 0;
    const n1 = noise(x * 0.085, z * 0.085);
    const hills = Math.max(0, n1) * 1.9 + Math.min(0, n1) * 0.4;
    const rv = noise2(x * 0.055 + 31, z * 0.055 - 17, 2);
    const ravine = -0.6 * (1 - smooth(0, 0.11, Math.abs(rv)));
    const grow = 0.35 + 0.65 * smooth(B + 2, B + 9, dh);
    const kRoad = smooth(0.8, 3.4, pd);
    const kWall = smooth(B + 1, B + 3.2, dh);
    const detail = noise(x * 0.6, z * 0.6, 2) * 0.06;
    let h = -0.05 + (hills + ravine) * grow * kRoad * kWall + detail * kWall;
    // A worn road, a little sunk between low verges.
    h = -0.11 + (h + 0.11) * smooth(0.45, 1.05, pd);
    return h * smooth(B + 0.6, B + 1.5, dh);
  };
  const ground = (x: number, z: number): number => heightWith(x, z, roadDist(x, z));

  const group = new THREE.Group();
  group.name = 'outskirts';

  // Ground: a jittered grid, faceted (flat shading) and painted per vertex.
  const g = low ? 0.62 : 0.45;
  const n = Math.ceil((extent * 2) / g);
  const vx = new Float32Array((n + 1) * (n + 1));
  const vz = new Float32Array((n + 1) * (n + 1));
  const vd = new Float32Array((n + 1) * (n + 1));
  const vt = new Uint8Array((n + 1) * (n + 1));
  const pos: number[] = [];
  const col: number[] = [];
  const c = new THREE.Color();
  const forestAt = (x: number, z: number): number => clamp01((noise(x * 0.11 + 101, z * 0.11 - 57, 2) + 0.12) * 2.4);
  for (let j = 0; j <= n; j++) {
    for (let i = 0; i <= n; i++) {
      const k = j * (n + 1) + i;
      const edge = i === 0 || j === 0 || i === n || j === n;
      const x = -extent + i * g + (edge ? 0 : (rnd() - 0.5) * g * 0.6);
      const z = -extent + j * g + (edge ? 0 : (rnd() - 0.5) * g * 0.6);
      vx[k] = x;
      vz[k] = z;
      vd[k] = hexDistAt(x, z);
      vt[k] = onTile(x, z) ? 1 : 0;
      const pd = roadDist(x, z);
      const h = heightWith(x, z, pd);
      // The mesh only: what stands on the ground (decor, lava, works) keeps the tiles' level.
      pos.push(x, vt[k] ? UNDER_TILE : h, z);
      // Paint: grass and heath patches, stone on the crests, dark in the ravines, dirt on the roads.
      const pn = noise(x * 0.35 + 7, z * 0.35 - 3, 2);
      c.copy(pn < -0.12 ? COL.grassA : pn < 0.15 ? COL.grassB : COL.moss);
      c.lerp(COL.heath, clamp01(noise(x * 0.07 - 40, z * 0.07 + 12, 2) * 2));
      const rel = h + 0.12;
      if (rel > 0.35) c.lerp(COL.stone, smooth(0.35, 0.95, rel) * 0.7);
      if (rel < -0.12) c.lerp(COL.ravine, smooth(-0.12, -0.55, rel) * 0.75);
      const forest = forestAt(x, z) * smooth(1.8, 4.2, pd);
      c.lerp(COL.floor, forest * 0.55);
      // Under the road ribbon: trodden dirt fading into darker verges.
      const vergeT = 1 - smooth(0.6, 1.25 + pn * 0.3, pd);
      if (vergeT > 0) c.lerp(COL.verge, vergeT * 0.7);
      const roadT = 1 - smooth(0.3, 0.7, pd);
      if (roadT > 0) c.lerp(COL.rut, roadT);
      const apronT = 1 - smooth(B + 0.8, B + 1.9, vd[k] as number);
      if (apronT > 0 && roadT < 0.5) c.lerp(COL.apron, apronT * 0.8);
      c.multiplyScalar(0.92 + rnd() * 0.12);
      col.push(c.r, c.g, c.b);
    }
  }
  const idx: number[] = [];
  for (let j = 0; j < n; j++) {
    for (let i = 0; i < n; i++) {
      const a = j * (n + 1) + i;
      const b = a + 1;
      const d = a + (n + 1);
      const e = d + 1;
      const hidden = (vt[a] && vt[b] && vt[d] && vt[e]) || ((vd[a] as number) < B + 0.45 && (vd[b] as number) < B + 0.45 && (vd[d] as number) < B + 0.45 && (vd[e] as number) < B + 0.45);
      const cx = ((vx[a] as number) + (vx[e] as number)) / 2;
      const cz = ((vz[a] as number) + (vz[e] as number)) / 2;
      if (hidden || cx * cx + cz * cz > extent * extent) continue;
      if ((i + j) % 2 === 0) idx.push(a, d, b, b, d, e);
      else idx.push(a, d, e, a, e, b);
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  geo.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
  geo.setIndex(idx);
  geo.computeVertexNormals();
  const groundMesh = new THREE.Mesh(geo, new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.97, metalness: 0, flatShading: true }));
  groundMesh.name = 'outskirts-ground';
  groundMesh.receiveShadow = true;
  group.add(groundMesh);

  // Woods, bushes, boulders, tufts: sparse near the roads and the walls, thick toward the fog.
  type Item = { m: THREE.Matrix4; c: THREE.Color };
  const trunks: Item[] = [];
  const cones: Item[] = [];
  const blobs: Item[] = [];
  const rocks: Item[] = [];
  const tufts: Item[] = [];
  const crystals: THREE.Matrix4[] = [];
  const q = new THREE.Quaternion();
  const e = new THREE.Euler();
  const put = (list: Item[], x: number, y: number, z: number, sx: number, sy: number, sz: number, color: THREE.Color, tilt = 0.12): void => {
    e.set((rnd() - 0.5) * tilt, rnd() * TAU, (rnd() - 0.5) * tilt);
    q.setFromEuler(e);
    list.push({ m: new THREE.Matrix4().compose(new THREE.Vector3(x, y, z), q, new THREE.Vector3(sx, sy, sz)), c: color });
  };
  const tint = (hex: string, v = 0.18): THREE.Color => new THREE.Color(hex).multiplyScalar(1 - v / 2 + rnd() * v);
  const pine = ['#24392a', '#2a4030', '#2f4a2a', '#22352c', '#34452c'];
  const leaf = ['#3c4a2a', '#45502e', '#36442a', '#4a4a2c'];
  const step = low ? 1.0 : 0.78;
  const cells = Math.ceil((outer * 2) / step);
  for (let j = 0; j <= cells; j++) {
    for (let i = 0; i <= cells; i++) {
      const x = -outer + i * step + (rnd() - 0.5) * step * 0.9;
      const z = -outer + j * step + (rnd() - 0.5) * step * 0.9;
      const r = Math.hypot(x, z);
      if (r > outer - 1.5) continue;
      const dh = hexDistAt(x, z);
      if (dh < B + 1.3) continue;
      const pd = roadDist(x, z);
      const rin = fogIn(x, z);
      const fogT = smooth(rin - 6.5, rin + 1, r);
      const wallT = smooth(B + 3, B + 5.5, dh);
      const forest = forestAt(x, z);
      const h = heightWith(x, z, pd);
      const roll = rnd();
      const treeP = wallT * smooth(2.0, 4.6, pd) * (0.06 + 0.94 * fogT * fogT) * (0.1 + 0.9 * forest) * (h < -0.45 ? 0.3 : 1);
      if (roll < treeP * 0.95) {
        const kind = rnd();
        if (kind < 0.68) {
          // Pine: trunk and two or three stacked cones.
          const tall = 1.0 + rnd() * 0.9 + fogT * 0.3;
          const w = 0.34 + rnd() * 0.12;
          const dead = rnd() < 0.05;
          const cc = dead ? tint('#4a4440') : tint(pine[Math.floor(rnd() * pine.length)] as string);
          put(trunks, x, h + 0.15, z, 0.07, 0.4, 0.07, tint('#3e2c1e'), 0.05);
          put(cones, x, h + 0.3 + tall * 0.3, z, w * 1.15, tall * 0.62, w * 1.15, cc, 0.06);
          put(cones, x, h + 0.3 + tall * 0.62, z, w * 0.85, tall * 0.5, w * 0.85, cc, 0.06);
          if (rnd() < 0.6) put(cones, x, h + 0.3 + tall * 0.9, z, w * 0.55, tall * 0.36, w * 0.55, cc, 0.06);
        } else {
          // Broadleaf: a gnarled trunk under a lumpy crown.
          const tall = 0.8 + rnd() * 0.5;
          const w = 0.42 + rnd() * 0.2;
          const cc = tint(leaf[Math.floor(rnd() * leaf.length)] as string);
          put(trunks, x, h + tall * 0.3, z, 0.08, tall * 0.65, 0.08, tint('#3a2a1e'), 0.15);
          put(blobs, x, h + tall * 0.7, z, w, w * 0.8, w, cc, 0.4);
          put(blobs, x + (rnd() - 0.5) * 0.3, h + tall * 0.85, z + (rnd() - 0.5) * 0.3, w * 0.7, w * 0.6, w * 0.7, cc.clone().multiplyScalar(1.08), 0.4);
        }
        continue;
      }
      if (roll < treeP * 0.95 + smooth(B + 2, B + 3.5, dh) * smooth(1.1, 2.2, pd) * (0.04 + 0.2 * forest)) {
        const s = 0.16 + rnd() * 0.16;
        put(blobs, x, h + s * 0.35, z, s * 1.3, s * 0.8, s * 1.3, tint('#2f3d27', 0.25), 0.3);
        continue;
      }
      if (rnd() < (0.012 + (h > 0.3 ? 0.06 : 0)) * smooth(2.2, 3.2, pd) * smooth(B + 2.5, B + 4, dh)) {
        // Boulder, sunk into the turf; now and then a cluster.
        const s = 0.22 + rnd() * (h > 0.25 ? 0.55 : 0.4);
        put(rocks, x, h + s * 0.25, z, s * (1 + rnd() * 0.5), s * (0.6 + rnd() * 0.4), s, tint('#4b4d4c', 0.2), 0.6);
        if (rnd() < 0.4) put(rocks, x + s * 0.9, h + s * 0.1, z + (rnd() - 0.5) * s, s * 0.45, s * 0.35, s * 0.45, tint('#45463f', 0.2), 0.6);
        continue;
      }
      if (rnd() < 0.5 * smooth(0.55, 0.9, pd)) {
        for (let t = 0; t < 2; t++) put(tufts, x + (rnd() - 0.5) * 0.4, h, z + (rnd() - 0.5) * 0.4, 0.07 + rnd() * 0.05, 0.12 + rnd() * 0.12, 0.07 + rnd() * 0.05, tint('#4b5631', 0.3), 0.3);
      }
      if (fogT > 0.5 && pd > 2 && rnd() < 0.006) for (let t = 0; t < 3; t++) {
        const m = new THREE.Matrix4();
        e.set((rnd() - 0.5) * 0.5, rnd() * TAU, (rnd() - 0.5) * 0.5);
        q.setFromEuler(e);
        const s = 0.06 + rnd() * 0.05;
        m.compose(new THREE.Vector3(x + (rnd() - 0.5) * 0.3, h, z + (rnd() - 0.5) * 0.3), q, new THREE.Vector3(s, 0.25 + rnd() * 0.3, s));
        crystals.push(m);
      }
    }
  }
  // The roads themselves: a smoothed ribbon with soft edges laid over the ground.
  group.add(roadRibbon(roads, ground, noise, B, hexDistAt));

  // Pebbles strewn along the roads.
  for (const pts of roads) {
    for (let i = 1; i < pts.length; i++) {
      const a = pts[i - 1] as THREE.Vector2;
      const b = pts[i] as THREE.Vector2;
      for (let k = 0; k < 2; k++) {
        if (rnd() > 0.6) continue;
        const t = rnd();
        const x = a.x + (b.x - a.x) * t + (rnd() - 0.5) * 0.7;
        const z = a.y + (b.y - a.y) * t + (rnd() - 0.5) * 0.7;
        const s = 0.035 + rnd() * 0.04;
        put(rocks, x, ground(x, z) + 0.01, z, s, s * 0.7, s, tint('#6d6559', 0.2), 0.5);
      }
    }
  }
  // Each kind in slices around the bastion, each with its own bounds: the camera culls the slices it doesn't see.
  // One mesh for the whole ring was always drawn whole, and a phone's view holds ~15 % of it (a Marea 8 wave on
  // SwiftShader at an Adreno 618's size: 74 → 60 ms a frame with only what is in view, 2026-10-31). `userData.ids`
  // keeps each piece's index in the kind's whole list (seasons pick its colour by it).
  const add = (geo: THREE.BufferGeometry, mat: THREE.Material, list: Item[], name: string): void => {
    if (list.length === 0) return;
    const at = new THREE.Vector3();
    const slices: number[][] = Array.from({ length: SLICES }, () => []);
    list.forEach((it, i) => {
      at.setFromMatrixPosition(it.m);
      const a = Math.atan2(at.z, at.x) / TAU + 0.5;
      (slices[Math.min(SLICES - 1, Math.floor(a * SLICES))] as number[]).push(i);
    });
    for (const ids of slices) {
      if (!ids.length) continue;
      const im = new THREE.InstancedMesh(geo, mat, ids.length);
      im.name = name;
      ids.forEach((k, i) => {
        const it = list[k] as Item;
        im.setMatrixAt(i, it.m);
        im.setColorAt(i, it.c);
      });
      im.userData.ids = Int32Array.from(ids);
      im.instanceMatrix.needsUpdate = true;
      if (im.instanceColor) im.instanceColor.needsUpdate = true;
      im.computeBoundingSphere();
      group.add(im);
    }
  };
  const flat = (rough = 0.95): THREE.MeshStandardMaterial => new THREE.MeshStandardMaterial({ roughness: rough, metalness: 0, flatShading: true });
  add(new THREE.CylinderGeometry(0.5, 0.75, 1, 5), flat(1), trunks, 'trunks');
  add(new THREE.ConeGeometry(1, 1, 7), flat(0.95), cones, 'cones');
  add(new THREE.IcosahedronGeometry(1, 0), flat(0.95), blobs, 'blobs');
  add(new THREE.DodecahedronGeometry(1, 0), flat(0.95), rocks, 'rocks');
  add(new THREE.ConeGeometry(0.5, 1, 4).translate(0, 0.5, 0), flat(1), tufts, 'tufts');
  if (crystals.length > 0) {
    const im = new THREE.InstancedMesh(
      new THREE.OctahedronGeometry(1, 0).scale(0.6, 1, 0.6).translate(0, 0.6, 0),
      new THREE.MeshStandardMaterial({ color: '#8fd3ff', emissive: '#2c7fd0', emissiveIntensity: 0.9, roughness: 0.15, flatShading: true }),
      crystals.length,
    );
    crystals.forEach((m, i) => im.setMatrixAt(i, m));
    im.instanceMatrix.needsUpdate = true;
    im.computeBoundingSphere();
    group.add(im);
  }

  const fog = new FogBank(spawns, base, outer, low, seed);
  return { ground, roadDist, group, fog };
};
